import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { MorningWelcome } from '../components/features/checkin/MorningWelcome';
import { MorningQuestions } from '../components/features/checkin/MorningQuestions';
import { MorningCompletion } from '../components/features/checkin/MorningCompletion';
import { templates } from '../content/question-library';
import { useCheckinStore } from '../stores/checkinStore';
import type { CheckinAnswerValue } from '../types/checkin';

type FlowStep = 'welcome' | 'questions' | 'complete';

export function MorningCheckinPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<FlowStep>('welcome');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedResponses, setCompletedResponses] = useState<Record<string, CheckinAnswerValue>>({});

  const { currentCheckIn, startMorningCheckIn, updateMorningResponse, submitCheckIn } = useCheckinStore();
  const questions = templates['daily-intentions'];

  // Extract just the answer values from QuestionResponse objects
  const responses = Object.entries(currentCheckIn?.responses || {}).reduce((acc, [id, response]) => {
    acc[id] = response.answer;
    return acc;
  }, {} as Record<string, CheckinAnswerValue>);

  const handleStart = () => {
    startMorningCheckIn('daily-intentions');
    setStep('questions');
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleResponseChange = (questionId: string, value: CheckinAnswerValue) => {
    updateMorningResponse(questionId, value);
  };

  const handleNext = async () => {
    if (currentIndex === questions.length - 1) {
      // Last question - save responses before submitting (store clears them)
      setCompletedResponses(responses);

      try {
        await submitCheckIn();
        setStep('complete');
      } catch (error) {
        console.error('Failed to submit check-in:', error);
        // TODO: Show error toast in future
      }
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      {/* Header with back button */}
      {step !== 'complete' && (
        <div className="px-4 pt-4 pb-2">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto py-8">
        {step === 'welcome' && (
          <MorningWelcome onStart={handleStart} onSkip={handleSkip} />
        )}

        {step === 'questions' && (
          <MorningQuestions
            questions={questions}
            currentIndex={currentIndex}
            responses={responses}
            onResponseChange={handleResponseChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSkip={handleSkip}
          />
        )}

        {step === 'complete' && (
          <MorningCompletion
            responses={completedResponses}
            questions={questions}
            onFinish={() => navigate('/dashboard')}
          />
        )}
      </div>
    </div>
  );
}
